/**
 * Date range selector: quick presets (incl. UK tax years) plus a custom From → To range.
 */
import { formatDate } from "@shared/dates.ts";
import { RANGE_PRESETS, type RangePreset } from "@shared/periods.ts";
import { useEffect, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import type { DateRangeState } from "../../lib/url-state.ts";
import { Icon } from "./Icon.tsx";

export function DateRangePicker({ range }: { range: DateRangeState }) {
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  useEffect(() => {
    setFrom(range.from);
    setTo(range.to);
  }, [range.from, range.to]);
  const invalid = !from || !to || to < from;

  return (
    <div className="date-range-picker d-flex flex-wrap align-items-center gap-2">
      <InputGroup size="sm" style={{ width: "auto" }}>
        <InputGroup.Text>
          <Icon name="calendar" size={14} />
        </InputGroup.Text>
        <Form.Select
          size="sm"
          aria-label="Date range"
          value={range.preset}
          onChange={(e) => range.set(e.target.value as RangePreset, { from, to })}
          style={{ minWidth: 170 }}
        >
          {RANGE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Form.Select>
      </InputGroup>
      {range.preset === "custom" ? (
        <form
          className="d-flex flex-wrap align-items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!invalid) range.set("custom", { from, to });
          }}
        >
          <Form.Control
            size="sm"
            type="date"
            aria-label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ width: 150 }}
          />
          <span className="text-muted">→</span>
          <Form.Control
            size="sm"
            type="date"
            aria-label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            isInvalid={!!from && !!to && to < from}
            style={{ width: 150 }}
          />
          <Button size="sm" type="submit" disabled={invalid}>
            Apply
          </Button>
        </form>
      ) : (
        <span className="small text-muted">
          {formatDate(range.from)} → {formatDate(range.to)}
        </span>
      )}
    </div>
  );
}
